import React, { useState } from 'react';
import { CheckCircleIcon } from '../../assets/icons/CheckCircleIcon';
import { TimesCircleIcon } from '../../assets/icons/TimesCircleIcon';
import { TrashIcon } from '../../assets/icons/TrashIcon';
import { ButtonThemes } from '../../libraries/ButtonTheme';
import { languageValues } from '../../utils/LanguageUtils';
import { ButtonProps } from '../button/Button';
import Modal, { ModalProps } from './Modal';

export interface AlertModalProps<Req = any, Res = any> extends ModalProps {
  language?: {
    confirm: string;
  };
  onConfirm: (obj?: Req) => void | Promise<Res>;
  disabled?: boolean;
  showIcons?: boolean;
}

export function AlertModal({
  disabled,
  language,
  onConfirm,
  showIcons = true,
  ...props
}: AlertModalProps) {
  const [loading, setLoading] = useState(false);

  return (
    <Modal
      footer={
        [
          {
            loading,
            disabled: loading || disabled,
            children:
              language?.confirm ??
              languageValues.components.modals.alertModal.confirm,
            onClick: () => {
              if (typeof onConfirm === 'function') {
                const retorno = onConfirm();
                if (retorno instanceof Promise) {
                  setLoading(true);
                  retorno.finally(() => {
                    setLoading(false);
                  });
                }
              }
            },
            theme: ButtonThemes.Primary,
            leftIcon: showIcons ? <CheckCircleIcon /> : undefined,
          },
        ] as ButtonProps[]
      }
      {...props}
    />
  );
}

export interface ConfirmModalProps<Req = any, Res = any> extends ModalProps {
  language?: {
    confirm: string;
    cancel: string;
  };
  onConfirm: (obj?: Req) => void | Promise<Res>;
  onCancel: () => void;
  showIcons?: boolean;
  disabled?: boolean;
}

export function ConfirmModal({
  disabled,
  language,
  onCancel,
  onConfirm,
  showIcons = true,
  ...props
}: ConfirmModalProps) {
  const [loading, setLoading] = useState(false);
  return (
    <Modal
      footer={[
        {
          children:
            language?.cancel ??
            languageValues.components.modals.confirmModal.cancel,
          onClick: onCancel,
          theme: ButtonThemes.Secondary,
          leftIcon: showIcons ? <TimesCircleIcon /> : undefined,
        },
        {
          loading,
          disabled: loading || disabled,
          children:
            language?.confirm ??
            languageValues.components.modals.confirmModal.confirm,
          onClick: () => {
            const retorno = onConfirm?.();
            if (retorno instanceof Promise) {
              setLoading(true);
              retorno.finally(() => {
                setLoading(false);
              });
            }
          },
          theme: ButtonThemes.Primary,
          leftIcon: showIcons ? <CheckCircleIcon /> : undefined,
        },
      ]}
      {...props}
    />
  );
}

export interface DestructiveModalProps extends ModalProps {
  language?: {
    confirm: string;
    cancel: string;
  };
  onConfirm: () => void | Promise<any>;
  disabled?: boolean;
  onCancel: () => void;
  showIcons?: boolean;
}

export function DestructiveModal({
  disabled,
  language,
  onCancel,
  onConfirm,
  showIcons = true,
  ...props
}: DestructiveModalProps) {
  const [loading, setLoading] = useState(false);
  return (
    <Modal
      footer={[
        {
          children:
            !language || !language.cancel
              ? languageValues.components.modals.destructiveModal.cancel
              : language.cancel,
          onClick: onCancel,
          theme: ButtonThemes.Default,
          leftIcon: showIcons ? <TimesCircleIcon /> : undefined,
        },
        {
          loading,
          disabled: loading || disabled,
          children:
            language?.confirm ??
            languageValues.components.modals.destructiveModal.confirm,
          onClick: () => {
            const retorno = onConfirm?.();
            if (retorno instanceof Promise) {
              setLoading(true);
              retorno.finally(() => {
                setLoading(false);
              });
            }
          },
          theme: ButtonThemes.Danger,
          leftIcon: showIcons ? <TrashIcon /> : undefined,
        },
      ]}
      {...props}
    />
  );
}
